/**
 * Metadatos de sincronización.
 *
 * Presentes desde la FASE 1 aunque la sincronización llegue en la FASE 8. Así
 * activarla es estrictamente aditivo y no obliga a migrar datos existentes.
 *
 * PRINCIPIO OBLIGATORIO (Ajuste 2 aprobado):
 * NINGUNA EDICIÓN VÁLIDA SE PIERDE SILENCIOSAMENTE.
 *
 * `baseVersion` es lo que hace posible cumplirlo sin CRDT ni merge por campo.
 * Escenario que debe detectarse:
 *
 *   1. El dispositivo A lee una fila cuya versión remota es V1 y la edita.
 *   2. Entretanto B edita la misma fila y sincroniza: la versión remota pasa a V2.
 *   3. A vuelve a conectarse y empuja.
 *
 * A envía `(id, baseVersion: V1, contenido)`. El servidor compara `baseVersion`
 * con el `updatedAt` actual:
 *   - iguales   → nadie tocó la fila. Se acepta. Es el 99 % de los casos.
 *   - distintos → hubo una edición intermedia. SE RECHAZA. No se sobreescribe.
 *
 * Al rechazarse, el cliente conserva AMBAS versiones y avisa sin bloquear.
 */

export interface MetadatosSync {
  /**
   * Momento de la última escritura.
   *
   * En la FASE 1 lo pone el dispositivo. En la FASE 8 el valor canónico lo
   * asigna el SERVIDOR (`now()` en Postgres), para que el desfase de relojes
   * entre el teléfono y la PC no decida quién gana.
   */
  updatedAt: string;

  /**
   * El `updatedAt` REMOTO sobre el que se basó la edición local.
   *
   * `null` = la fila nunca se sincronizó (nació local y no se ha empujado).
   * Es el detector de conflicto: si no coincide con la versión remota actual,
   * hubo una edición intermedia.
   */
  baseVersion: string | null;

  /**
   * Hay cambios locales sin empujar.
   *
   * Es la cola de sincronización: no hace falta una tabla aparte.
   *
   * NO va en ninguna cadena de índices de Dexie: IndexedDB no indexa booleanos
   * de forma confiable. Se filtra en memoria en el repositorio.
   */
  dirty: boolean;

  /**
   * Soft delete. Un borrado duro no se propaga entre dispositivos: el otro
   * dispositivo simplemente no se entera y vuelve a subir la fila.
   *
   * Tampoco va en índices, por la misma razón que `dirty`.
   */
  deleted: boolean;
}

/** Metadatos de una fila recién creada en este dispositivo. */
export function metadatosNuevos(ahora: Date = new Date()): MetadatosSync {
  return {
    updatedAt: ahora.toISOString(),
    baseVersion: null,
    dirty: true,
    deleted: false,
  };
}

/**
 * Metadatos tras una edición local.
 *
 * `baseVersion` NO se toca: sigue apuntando a la última versión remota
 * conocida, que es exactamente lo que el servidor tiene que comparar. Pisarlo
 * aquí destruiría la detección de conflictos.
 */
export function metadatosEditados(
  previos: MetadatosSync,
  ahora: Date = new Date(),
): MetadatosSync {
  return {
    updatedAt: ahora.toISOString(),
    baseVersion: previos.baseVersion,
    dirty: true,
    deleted: previos.deleted,
  };
}

/** Metadatos tras un borrado lógico. */
export function metadatosBorrados(
  previos: MetadatosSync,
  ahora: Date = new Date(),
): MetadatosSync {
  return { ...metadatosEditados(previos, ahora), deleted: true };
}

/** Campos de sincronización, para poder quitarlos del JSON exportado. */
export const CAMPOS_SYNC = ['updatedAt', 'baseVersion', 'dirty', 'deleted'] as const;

/**
 * Quita los metadatos de sincronización de un objeto.
 *
 * El JSON de respaldo NUNCA debe llevarlos: son detalle interno, y filtrarlos
 * rompería el formato para cualquier otro consumidor (CA-4.8).
 */
export function sinMetadatosSync<T extends Partial<MetadatosSync>>(
  fila: T,
): Omit<T, keyof MetadatosSync> {
  const copia = { ...fila } as Record<string, unknown>;
  for (const campo of CAMPOS_SYNC) delete copia[campo];
  return copia as Omit<T, keyof MetadatosSync>;
}
